import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { 
  ShieldCheck, 
  Users, 
  FlaskConical, 
  Package, 
  TrendingUp, 
  Plus, 
  Trash2, 
  Check, 
  FileText, 
  Upload, 
  Eye, 
  DollarSign, 
  Calendar,
  AlertCircle,
  Loader2,
  X,
  RefreshCw,
  Building2,
  MapPin
} from "lucide-react";
import AdminCorporateTab from "../components/admin/AdminCorporateTab";
import AdminPharmacyTab from "../components/admin/AdminPharmacyTab";
import AdminCouriersTab from "../components/admin/AdminCouriersTab";
import AdminShiprocketCentre from "../components/admin/AdminShiprocketCentre";
import AdminNotificationsTab from "../components/admin/AdminNotificationsTab";
import AdminPatientDbTab from "../components/admin/AdminUsersTab";
import AdminCommandPalette from "../components/admin/AdminCommandPalette";
import AdminCommandCenter from "../components/admin/AdminCommandCenter";
import AdminAlertsTab from "../components/admin/AdminAlertsTab";
import AdminIntegrationsTab from "../components/admin/AdminIntegrationsTab";
import AdminAuthPolicyTab from "../components/admin/AdminAuthPolicyTab";
import AdminAccessTab from "../components/admin/AdminAccessTab";
import AdminDoctorAccessTab from "../components/admin/AdminDoctorAccessTab";
import AdminWorkspacesTab from "../components/admin/AdminWorkspacesTab";
import AdminMembershipTab from "../components/admin/AdminMembershipTab";
import AdminHomeCollectionTab from "../components/admin/AdminHomeCollectionTab";
import { useLiveEvents } from "../lib/useLiveEvents";
import AdminDoctorsTab from "../components/admin/AdminDoctorsTab";
import AdminDoctorApplications from "../components/admin/AdminDoctorApplications";
import AdminNotificationCentre from "../components/admin/AdminNotificationCentre";
import AdminSmsProviders from "../components/admin/AdminSmsProviders";
import AdminSmsTemplateRegistry from "../components/admin/AdminSmsTemplateRegistry";
import AdminFinanceTab from "../components/admin/AdminFinanceTab";
import AdminPaymentsTab from "../components/admin/AdminPaymentsTab";
import AdminRolesTab from "../components/admin/AdminRolesTab";
import AdminDiagnosticsV2Tab from "../components/admin/AdminDiagnosticsV2Tab";
import AdminDocTemplates from "../components/admin/AdminDocTemplates";
import AdminPhysio from "./physio/AdminPhysio";
import AdminFeatureControls from "./staff/AdminFeatureControls";
import PhysioDesk from "./physio/PhysioDesk";
import PhysioWorkspace from "./physio/PhysioWorkspace";
import LabWorkspace from "./staff/lab/LabWorkspace";
import RadiologyWorkspace from "./staff/lab/RadiologyWorkspace";
import LabReports from "./staff/LabReports";
import FrontDesk from "./staff/FrontDesk";
import { AdminCouponsTab, AdminAuditTab } from "../components/admin/AdminCouponsAuditTabs";
import AdminCatalogueTab from "../components/admin/AdminCatalogueTab";
import AdminHealthLibraryTab from "../components/admin/AdminHealthLibraryTab";
import AdminReviewsTab from "../components/admin/AdminReviewsTab";
import AdminForumTab from "../components/admin/AdminForumTab";
import { useAccessLive } from "../lib/useAccessLive";
import AdminCmsTab, { KINDS } from "../components/admin/AdminCmsTab";
import AdminSettingsTab from "../components/admin/AdminSettingsTab";
import AdminPagesTab from "../components/admin/AdminPagesTab";
import { AdminMediaTab, AdminDashboardHome, AdminUsersTab, DoctorForm } from "../components/admin/AdminCmsExtras";
import { Modal } from "../components/admin/cmsShared";
import { useSite } from "../context/SiteContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const CATALOGUE_ADMIN_ROLES = ["admin", "super_admin"];
const STAFF_ROLES = ["admin", "super_admin", "pharmacy_staff", "membership_staff", "receptionist", "finance"];

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const { reload: reloadSite } = useSite();
  const [perms, setPerms] = useState([]); const [permStrings, setPermStrings] = useState([]);
  const reloadPerms = () => axios.get(`${API}/admin/permissions/me`, { withCredentials: true }).then((r) => { setPerms(r.data.modules); setPermStrings(r.data.role === "super_admin" ? ["*"] : (r.data.permissions || [])); }).catch(() => {});
  useAccessLive(reloadPerms, { enabled: !!user && STAFF_ROLES.includes(user.role) });
  const [doctorModal, setDoctorModal] = useState(null);
  const [group, setGroup] = useState("operations");
  const [overview, setOverview] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [tests, setTests] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("command");
  const [focus, setFocus] = useState(null); // {tab, id, metric, ts} — deep-link target from palette / command center / alerts
  

  // Report Upload Form State
  const [reportForm, setReportForm] = useState({
    booking_id: "LAB-DEMO99",
    patient_name: "Sourav Ganguly",
    patient_email: "patient@6sigmacare.in",
    test_name: "Complete Blood Count (CBC) with ESR",
    category: "Pathology",
    performed_date: new Date().toISOString().split("T")[0],
    doctor_or_pathologist: "Dr. Tanmoy Ghosh (MD Pathology)",
    overall_interpretation: "Normocytic normochromic RBCs. Normal platelet count and WBC differential.",
    findings: [
      { parameter: "Haemoglobin", value: "14.5", unit: "g/dL", normal_range: "13.0 - 17.0", status: "Normal" },
      { parameter: "Total Leukocyte Count", value: "6,800", unit: "cells/cu.mm", normal_range: "4,000 - 10,000", status: "Normal" },
      { parameter: "Platelet Count", value: "2.80", unit: "lakh/cu.mm", normal_range: "1.50 - 4.50", status: "Normal" }
    ]
  });
  const [uploadSuccess, setUploadSuccess] = useState("");

  useEffect(() => {
    if (user && STAFF_ROLES.includes(user.role)) {
      fetchAllData();
      axios.get(`${API}/admin/permissions/me`, { withCredentials: true }).then((r) => { setPerms(r.data.modules); setPermStrings(r.data.role === "super_admin" ? ["*"] : (r.data.permissions || [])); }).catch(() => {});
    } else {
      setLoading(false);
    }
  }, [user]);

  useLiveEvents(["appointment", "diagnostics", "payment", "finance", "doctor", "order", "physio", "enquiry", "collection"], () => { if (user && STAFF_ROLES.includes(user.role)) fetchAllData(true); });
  const fetchAllData = async (quiet = false) => {
    if (quiet !== true) setLoading(true);
    try {
      const [overRes, docRes, testRes, pkgRes] = await Promise.all([
        axios.get(`${API}/admin/overview`, { withCredentials: true }),
        axios.get(`${API}/doctors?include_inactive=1`),
        axios.get(`${API}/diagnostics`),
        axios.get(`${API}/packages`)
      ]);
      setOverview(overRes.data);
      setDoctors(docRes.data || []);
      setTests(testRes.data || []);
      setPackages(pkgRes.data || []);
    } catch (err) {
      console.error("Error fetching admin data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadReport = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/reports/upload`, reportForm, { withCredentials: true });
      setUploadSuccess("Medical report successfully published to patient portal!");
      setTimeout(() => setUploadSuccess(""), 4000);
      fetchAllData();
    } catch (err) {
      console.error("Error uploading report:", err);
    }
  };

  if (!user || !STAFF_ROLES.includes(user.role)) {
    return (
      <div className="max-w-md mx-auto my-20 p-8 bg-white rounded-3xl border border-gray-200 shadow-xl text-center space-y-4" data-testid="admin-access-denied">
        <ShieldCheck className="w-12 h-12 text-amber-600 mx-auto" />
        <h2 className="font-serif-luxury text-2xl font-bold text-gray-900">Access denied</h2>
        <p className="text-xs text-gray-500">This console is restricted to authorized 6SigmaCare administrators.</p>
      </div>
    );
  }

  const { stats = {}, recent_bookings = [] } = overview || {};
  const can = (m) => perms.includes(m);
  const GROUPS = {
    operations: { label: "Operations", tabs: [
      { key: "command", label: "Command Center", icon: TrendingUp },
    ...(can("alerts") ? [{ key: "alerts", label: "Alert Center", icon: AlertCircle }] : []),
    ...(can("workspaces") ? [{ key: "workspaces", label: "Workspaces", icon: ShieldCheck }] : []),
    { key: "overview", label: "Dashboard (classic)", icon: TrendingUp },
      ...(can("bookings") ? [{ key: "bookings", label: `Recent Bookings (${recent_bookings.length})`, icon: Calendar }] : []),
      ...(can("reports") ? [{ key: "upload_report", label: "Publish Report", icon: Upload }] : []),
      ...(can("corporate") ? [{ key: "corporate", label: "Corporate / B2B Healthcare", icon: Building2 }, { key: "home_collection", label: "Home Collection Roster", icon: MapPin }] : []),
    ] },
    money: { label: "Finance", tabs: [
      ...(can("finance") ? [{ key: "payments", label: "Payments, Gateways & Ledger", icon: TrendingUp }] : []),
    ] },
    consult: { label: "Doctor Consultations", tabs: [
      ...(can("doctors") ? [{ key: "doctors", label: `Doctors, Modes & Schedules (${doctors.length})`, icon: Users }, { key: "doctor_applications", label: "Doctor Applications · Self-enrolment Review", icon: ShieldCheck }] : []),
      ...(can("doctor_access") ? [{ key: "doctor_access", label: "Doctor Accounts / Portal Access", icon: ShieldCheck }] : []),
      ...(can("reception") ? [{ key: "reception", label: "Reception & OPD Queue", icon: Calendar }] : []),
      ...(can("finance") ? [{ key: "finance", label: "Doctor Finance & Settlements", icon: TrendingUp }] : []),
    ] },
    catalogue: { label: "Catalogue & Pricing", tabs: can("catalogue") ? [
      { key: "labops", label: "Diagnostics Command Centre · Lab operations", icon: FileText }, { key: "physio", label: "Physiotherapy · services, therapists, packages, policies", icon: FileText }, { key: "physio-ws", label: "Physiotherapy · workspace & desk", icon: FileText },
      { key: "radops", label: "Radiology · Schedule, Studies & Reports", icon: FileText },
      { key: "diagv2", label: "Diagnostics v2 · Test Master, Quality & Orders", icon: Package },
      { key: "diagreports", label: "Diagnostic Reports · Upload, Verify & Release", icon: FileText },
      { key: "tests", label: `Tests, Categories & TAT (${tests.length})`, icon: FlaskConical }, { key: "packages", label: `Health Packages (${packages.length})`, icon: Package },
      { key: "cms:specialties", label: "Specialties", icon: Users }, { key: "pricing", label: "Pricing Hub & Booking Rules", icon: TrendingUp }, { key: "coupons", label: "Promo Codes", icon: Package },
    ] : [] },
    website: { label: "Website CMS", tabs: (can("content") || can("health_library") || can("reviews") || can("forum")) ? [
      ...(can("reviews") ? [{ key: "reviews", label: "Reviews & Reputation", icon: FileText }] : []),
      ...(can("forum") ? [{ key: "forum", label: "Community Forum", icon: Users }] : []),
      ...(can("health_library") ? [{ key: "health_library", label: "Health Library · Articles & Blog", icon: FileText }] : []),
      ...(can("content") ? [{ key: "pages", label: "Pages & Homepage Builder", icon: FileText }] : []), { key: "cms:nav", label: "Navigation Menu", icon: FileText }, { key: "settings:header", label: "Header", icon: FileText }, { key: "settings:footer", label: "Footer", icon: FileText }, { key: "cms:footer_sections", label: "Footer Sections", icon: FileText },
      { key: "cms:announcements", label: "Announcements & Offers", icon: AlertCircle }, { key: "cms:services", label: "Services", icon: FlaskConical }, { key: "cms:stats", label: "Statistics", icon: TrendingUp }, { key: "cms:why_us", label: "Why Choose Us", icon: Check },
      { key: "cms:testimonials", label: "Testimonials", icon: Users }, { key: "cms:faqs", label: "FAQs", icon: FileText }, { key: "cms:gallery", label: "Gallery", icon: Eye }, { key: "cms:facilities", label: "Facilities", icon: Building2 }, { key: "cms:certifications", label: "Certifications", icon: ShieldCheck }, { key: "cms:timeline", label: "Timeline", icon: Calendar },
      { key: "cms:legal_pages", label: "Legal & Policy Pages", icon: FileText }, { key: "cms:seo", label: "SEO", icon: Eye }, { key: "cms:ctas", label: "Buttons & CTAs", icon: Plus }, { key: "media", label: "Media Manager", icon: Upload },
    ] : [] },
    pharmacy: { label: "Pharmacy", tabs: can("pharmacy") ? [{ key: "pharmacy", label: "Pharmacy Management", icon: Package }, { key: "couriers", label: "Courier Providers & Logistics", icon: Package }] : [] },
    membership: { label: "Membership", tabs: can("membership") ? [{ key: "membership", label: "6SigmaCare Membership", icon: ShieldCheck }] : [] },
    settings: { label: "Settings & Security", tabs: [
      ...(can("settings") ? [{ key: "settings:general", label: "Global Settings", icon: ShieldCheck }, { key: "features", label: "Service & Feature Controls · Physiotherapy, Procedures, modules", icon: ShieldCheck }, { key: "documents", label: "Document Templates · Branding & Issued PDFs", icon: FileText }, { key: "notifications", label: "Notification Engine · SMS, Email, WhatsApp, In-app", icon: ShieldCheck }, { key: "comms_centre", label: "Communications · Notification Centre & Usage", icon: ShieldCheck }, { key: "sms_providers", label: "Integrations · SMS Providers (Fast2SMS, Twilio, MSG91, 2Factor)", icon: ShieldCheck }, { key: "sms_templates", label: "Communications · SMS · MSG91 Template Registry", icon: ShieldCheck }, { key: "settings:contact", label: "Contact Page", icon: MapPin }] : []),
      ...(can("users") ? [{ key: "patientdb", label: "Users / Patients Database", icon: Users }, { key: "users", label: "Staff Accounts", icon: Users }, { key: "roles", label: "Roles & Permissions", icon: Users }] : []),
      ...(can("access") ? [{ key: "access", label: "Staff & Access Management", icon: ShieldCheck }] : []),
    ...(can("integrations") ? [{ key: "integrations", label: "Integrations", icon: ShieldCheck }] : []),
    ...(can("security") ? [{ key: "authpolicy", label: "Authentication Policy", icon: ShieldCheck }] : []),
    ...(can("audit") ? [{ key: "audit", label: "Audit Log", icon: ShieldCheck }] : []),
    ] },
  };
  const openTarget = (t) => { if (!t?.tab) return; const g = Object.entries(GROUPS).find(([, gr]) => gr.tabs.some((x) => x.key === t.tab)); if (g) setGroup(g[0]); setActiveTab(t.tab); setFocus({ ...t, ts: Date.now() }); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8 space-y-6" data-testid="admin-dashboard-page">
      
      {/* Header */}
      <div className="bg-[#07221A] text-white p-5 sm:p-7 rounded-3xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase bg-amber-400 text-[#07221A] px-2 py-0.2 rounded">
              CMS & Administration
            </span>
            <span className="text-xs text-emerald-300" data-testid="admin-logged-in-as">Logged in as: <b>{user.name}</b> ({user.role})</span>
          </div>
          <h1 className="font-serif-luxury text-2xl sm:text-3xl font-bold text-white mt-1">
            6SigmaCare Polyclinic Operations & CMS
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
        <AdminCommandPalette onOpen={openTarget} />
        <button
          onClick={fetchAllData}
          data-testid="admin-refresh-btn"
          className="px-4 py-2 bg-emerald-800 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh Live Metrics
        </button>
        </div>
      </div>

      {/* Tabs */}
      {(() => {
        return (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1 bg-[#0D3B2E] rounded-2xl p-1 text-xs font-bold">{Object.entries(GROUPS).filter(([, g]) => g.tabs.length).map(([k, g]) => <button key={k} onClick={() => { setGroup(k); setActiveTab(g.tabs[0].key); }} data-testid={`admin-group-${k}`} className={`px-4 py-2 rounded-xl ${group === k ? "bg-white text-[#0D3B2E]" : "text-emerald-100 hover:bg-white/10"}`}>{g.label}</button>)}</div>
            <select value={activeTab} onChange={(e) => setActiveTab(e.target.value)} data-testid="admin-tab-select" aria-label="Section" className="sm:hidden w-full px-3 py-2.5 bg-white border border-gray-200 rounded-2xl text-xs font-semibold text-gray-800 shadow-sm">
              {(GROUPS[group]?.tabs || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <div className="hidden sm:flex flex-wrap gap-1 border-b border-gray-200 bg-white rounded-2xl p-1 shadow-sm text-xs font-semibold text-gray-600">
              {(GROUPS[group]?.tabs || []).map((t) => { const IconC = t.icon; return (
                <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`admin-tab-${t.key.replace(":", "-")}`} className={`flex items-center gap-2 px-3 py-2 rounded-xl whitespace-nowrap transition-all ${activeTab === t.key ? "bg-[#0D3B2E] text-white shadow" : "hover:bg-gray-100 text-gray-700"}`}><IconC className="w-3.5 h-3.5" /><span>{t.label}</span></button>); })}
            </div>
          </div>
        );
      })()}

      {/* 1. OVERVIEW STATS */}
      {activeTab === "command" && <AdminCommandCenter onOpen={openTarget} />}
      {activeTab === "alerts" && <AdminAlertsTab onOpen={openTarget} canConfigure={can("settings")} />}
      {activeTab === "workspaces" && <AdminWorkspacesTab />}
      {activeTab === "access" && <AdminAccessTab user={user} focus={focus} />}
      {activeTab === "doctor_access" && <AdminDoctorAccessTab user={user} focus={focus} />}
      {activeTab === "integrations" && <AdminIntegrationsTab onOpen={openTarget} />}
      {activeTab === "authpolicy" && <AdminAuthPolicyTab />}
      {activeTab === "overview" && <AdminDashboardHome />}

      {/* 2. CATALOGUE CMS: TESTS · CATEGORIES · PRICING · TAT */}
      {activeTab === "tests" && (
        <div className="space-y-3">
          <div>
            <h3 className="font-serif-luxury text-xl font-bold text-gray-900">Investigations Catalogue, Pricing & TAT</h3>
            <p className="text-xs text-gray-500">Add or edit tests, categories, MRP, discounts and report turnaround — changes reflect live on the patient-facing Diagnostics page.</p>
          </div>
          <AdminCatalogueTab canEdit={CATALOGUE_ADMIN_ROLES.includes(user.role)} initialView="tests" onChanged={fetchAllData} />
        </div>
      )}

      {/* 3. DOCTORS MANAGEMENT */}
      {activeTab === "doctors" && <AdminDoctorsTab user={user} focus={focus} />}
      {activeTab === "doctor_applications" && <AdminDoctorApplications />}
      {activeTab === "finance" && <AdminFinanceTab user={user} />}
      {activeTab === "payments" && <AdminPaymentsTab user={user} focus={focus} />}
      {activeTab === "roles" && <AdminRolesTab user={user} />}
      {activeTab === "diagv2" && <AdminDiagnosticsV2Tab user={user} focus={focus} />}
      {activeTab === "labops" && <LabWorkspace user={user} focus={focus} />}
      {activeTab === "radops" && <RadiologyWorkspace user={user} focus={focus} />}
      {activeTab === "diagreports" && <LabReports user={user} focus={focus} />}

      {/* 4. HEALTH PACKAGES CMS */}
      {activeTab === "packages" && (
        <div className="space-y-3">
          <h3 className="font-serif-luxury text-xl font-bold text-gray-900">Preventive Health Packages Management</h3>
          <AdminCatalogueTab canEdit={CATALOGUE_ADMIN_ROLES.includes(user.role)} initialView="packages" onChanged={fetchAllData} />
        </div>
      )}

      {/* 5. LAB & IMAGING REPORT UPLOADER */}
      {activeTab === "upload_report" && (
        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm space-y-6 max-w-3xl">
          <div>
            <h3 className="font-serif-luxury text-xl font-bold text-gray-900">Lab & Radiology Report Publishing Engine</h3>
            <p className="text-xs text-gray-500">Generate verified digital reports directly accessible by patients in My Health.</p>
          </div>

          {uploadSuccess && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-xl flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-600" />
              <span>{uploadSuccess}</span>
            </div>
          )}

          <form onSubmit={handleUploadReport} className="space-y-4 text-xs">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Booking Ref ID</label>
                <input
                  type="text"
                  required
                  value={reportForm.booking_id}
                  onChange={(e) => setReportForm({ ...reportForm, booking_id: e.target.value })}
                  className="w-full px-3 py-2 bg-[#FBF9F4] border rounded-xl"
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Investigation Name</label>
                <input
                  type="text"
                  required
                  value={reportForm.test_name}
                  onChange={(e) => setReportForm({ ...reportForm, test_name: e.target.value })}
                  className="w-full px-3 py-2 bg-[#FBF9F4] border rounded-xl"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Patient Full Name</label>
                <input
                  type="text"
                  required
                  value={reportForm.patient_name}
                  onChange={(e) => setReportForm({ ...reportForm, patient_name: e.target.value })}
                  className="w-full px-3 py-2 bg-[#FBF9F4] border rounded-xl"
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Patient Email Address</label>
                <input
                  type="email"
                  required
                  value={reportForm.patient_email}
                  onChange={(e) => setReportForm({ ...reportForm, patient_email: e.target.value })}
                  className="w-full px-3 py-2 bg-[#FBF9F4] border rounded-xl"
                />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-1">Overall Clinical Interpretation & Impression</label>
              <textarea
                rows={3}
                required
                value={reportForm.overall_interpretation}
                onChange={(e) => setReportForm({ ...reportForm, overall_interpretation: e.target.value })}
                className="w-full px-3 py-2 bg-[#FBF9F4] border rounded-xl"
              />
            </div>

            <button
              type="submit"
              data-testid="submit-report-upload-btn"
              className="w-full py-3 bg-[#0D3B2E] text-white font-semibold rounded-xl hover:bg-[#165644] flex items-center justify-center gap-2"
            >
              <Upload className="w-4 h-4 text-amber-300" />
              Sign & Publish Digital Medical Report
            </button>
          </form>
        </div>
      )}

      {/* 6. BOOKINGS LIST */}
      {activeTab === "bookings" && (
        <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
          <h3 className="font-serif-luxury text-xl font-bold text-gray-900">All Scheduled Consultations & Investigations</h3>
          <div className="space-y-3">
            {recent_bookings.map((b, i) => (
              <div key={i} className="p-4 bg-[#FBF9F4] rounded-2xl border border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 text-xs">
                <div className="min-w-0">
                  <p className="font-bold text-gray-900">{b.patient_name} — {b.doctor_name || b.test_name || b.package_name || b.items?.[0]?.name}</p>
                  <p className="text-gray-500 break-all">Date: {b.date} ({b.time_slot}) • Ref: <b className="font-mono">{b.id}</b></p>
                </div>
                <div className="flex flex-wrap sm:flex-col sm:items-end items-center gap-2 sm:gap-1 sm:text-right shrink-0">
                  <span className="font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded">{b.status}</span>
                  <span className="text-[11px] text-gray-500">{b.payment_status} (₹{b.amount || b.total_amount})</span>
                  {(b.type === "diagnostic_test" || b.type === "health_package") && (
                    <select
                      value={b.sample_stage || "Booked"}
                      data-testid={`sample-stage-${b.id}`}
                      onChange={async (e) => { await axios.put(`${API}/admin/samples/${b.id}/stage`, null, { params: { stage: e.target.value, booking_type: b.type === "health_package" ? "package" : "diagnostic" }, withCredentials: true }); fetchAllData(); }}
                      className="px-2 py-1 bg-white border border-gray-200 rounded-lg text-[11px]"
                    >
                      {["Booked", "Sample Collected", "Received at Lab", "Processing", "Report Ready"].map((st) => <option key={st}>{st}</option>)}
                    </select>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "pharmacy" && <AdminPharmacyTab user={user} focus={focus} />}
      {activeTab === "couriers" && <div className="space-y-6"><AdminShiprocketCentre user={user} /><AdminCouriersTab user={user} /></div>}
      {activeTab === "notifications" && <AdminNotificationsTab />}
      {activeTab === "comms_centre" && <AdminNotificationCentre />}
      {activeTab === "sms_providers" && <AdminSmsProviders />}
      {activeTab === "sms_templates" && <AdminSmsTemplateRegistry user={user} />}
      {activeTab === "patientdb" && <AdminPatientDbTab focus={focus} />}
      {activeTab === "membership" && <AdminMembershipTab user={user} focus={focus} />}
      {activeTab === "corporate" && <AdminCorporateTab />}
      {activeTab === "home_collection" && <AdminHomeCollectionTab focus={focus} />}
      {activeTab === "reception" && <FrontDesk user={user} focus={focus} />}
      {activeTab === "coupons" && <AdminCouponsTab />}
      {activeTab === "audit" && <AdminAuditTab />}
      {activeTab === "documents" && <AdminDocTemplates user={user} />}
      {activeTab === "physio" && <AdminPhysio user={user} />}
      {activeTab === "features" && <AdminFeatureControls user={user} />}
      {activeTab === "physio-ws" && <div className="space-y-6"><PhysioDesk user={user} /><PhysioWorkspace user={user} /></div>}
      {activeTab === "pages" && <AdminPagesTab />}
      {activeTab === "health_library" && <AdminHealthLibraryTab perms={permStrings} />}
      {activeTab === "reviews" && <AdminReviewsTab perms={permStrings} />}
      {activeTab === "forum" && <AdminForumTab perms={permStrings} />}
      {activeTab === "media" && <AdminMediaTab />}
      {activeTab === "users" && <AdminUsersTab />}
      {activeTab === "pricing" && <AdminSettingsTab section="pricing" />}
      {activeTab.startsWith("settings:") && <AdminSettingsTab section={activeTab.split(":")[1]} />}
      {activeTab.startsWith("cms:") && KINDS[activeTab.split(":")[1]] && <AdminCmsTab kind={activeTab.split(":")[1]} onChanged={reloadSite} />}
      {doctorModal && <Modal title={doctorModal.id ? `Edit doctor · ${doctorModal.name}` : "Add doctor"} onClose={() => setDoctorModal(null)} testId="doctor-modal"><DoctorForm doctor={doctorModal.id ? doctorModal : null} onSaved={() => { setDoctorModal(null); fetchAllData(); }} onClose={() => setDoctorModal(null)} /></Modal>}

    </div>
  );
}
